import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import {
  useGetOrganizationHallTimesQuery,
  useReleaseOrganizationHallTimeMutation,
  useRestoreOrganizationHallTimeMutation,
} from "api/account/accountApiSlice";
import { HallTime } from "api/account/types";
import AccountPageLayout from "../components/AccountPageLayout";
import { ORGANIZATION_ID_PARAM, makeOrganizationBookingsPath } from "../common/organizationAdmin";
import {
  FIRST_WEEKS,
  HallTimeAction,
  MAX_WEEKS,
  MORE_WEEKS,
  hallTimeAction,
  hallTimeFailureKey,
} from "../common/organizationHallTimes";

/**
 * "Our hall times" (W2-1, M6): the organisation's allocation windows, date by
 * date. Every member reads them; an owner releases a date until it starts, so
 * others can book that time, and takes the release back (Q7 A). A date the
 * room's window pauses on (a holiday, D7) is listed and marked, with no
 * action. Nothing is mailed (Q8 A). What may be done is decided by the server (`canManage`,
 * `started`), and every refusal is told apart.
 */
const OrganizationHallTimesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const organizationId = String(params[ORGANIZATION_ID_PARAM] ?? "");
  const [weeks, setWeeks] = React.useState(FIRST_WEEKS);

  const { data, isLoading, isError, error, isFetching } = useGetOrganizationHallTimesQuery(
    { organizationId, weeks },
    { skip: organizationId.length === 0 }
  );
  const [release, { isLoading: isReleasing }] = useReleaseOrganizationHallTimeMutation();
  const [restore, { isLoading: isRestoring }] = useRestoreOrganizationHallTimeMutation();

  const [pending, setPending] = React.useState<{ time: HallTime; action: HallTimeAction } | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  // The organisation is context: switching must not carry a half-confirmed release.
  React.useEffect(() => {
    setWeeks(FIRST_WEEKS);
    setPending(null);
    setMessage(null);
  }, [organizationId]);

  const p = (key: string, options?: Record<string, unknown>) => t(`account.organization_hall_times.${key}`, options);

  // The room's location wall clock (ADR-0019), not the phone's.
  const formatWhen = (time: HallTime): string => {
    const lang = i18n.language;
    const from = new Date(time.from);
    const to = new Date(time.to);
    const day = from.toLocaleDateString(lang, {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: time.timeZone,
    });
    const clock = (d: Date) =>
      d.toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit", timeZone: time.timeZone });
    return `${day} · ${clock(from)}–${clock(to)}`;
  };

  const confirm = async () => {
    if (!pending) return;
    const { time, action } = pending;
    setMessage(null);
    try {
      const args = { organizationId, windowId: time.windowId, date: time.date };
      await (action === "release" ? release(args) : restore(args)).unwrap();
      setMessage(p(action === "release" ? "released_done" : "restored_done", { when: formatWhen(time) }));
    } catch (err) {
      const e = err as { status?: number | string; data?: { code?: string } };
      setMessage(p(hallTimeFailureKey(e?.status, e?.data?.code)));
    } finally {
      setPending(null);
    }
  };

  const backToPlan = (
    <Button variant="outline" className="w-full" onClick={() => navigate(makeOrganizationBookingsPath(organizationId))}>
      {p("back_to_plan")}
    </Button>
  );

  if (isLoading) {
    return (
      <AccountPageLayout title={p("title")}>
        <p className="text-sm text-slate-600">{t("common.loading")}</p>
      </AccountPageLayout>
    );
  }

  if (isError || !data) {
    const status = (error as { status?: number } | undefined)?.status;
    return (
      <AccountPageLayout title={p("title")}>
        <p className="text-sm text-slate-600">{status === 404 ? p("error_not_allowed") : p("load_error")}</p>
        {backToPlan}
      </AccountPageLayout>
    );
  }

  const times = Array.isArray(data.times) ? data.times : [];
  const busy = isReleasing || isRestoring;

  return (
    <AccountPageLayout title={p("title")}>
      <p className="text-sm text-slate-600">
        {p(data.canManage ? "intro_owner" : "intro_member", { organization: data.organizationName })}
      </p>

      {message && (
        <div className="rounded-md border border-foreground/15 px-3 py-2 text-sm text-foreground">{message}</div>
      )}

      {times.length === 0 ? (
        <p className="text-sm text-slate-600">{p(data.hasWindows ? "empty_span" : "empty")}</p>
      ) : (
        <ul className="space-y-2">
          {times.map((time) => {
            const action = data.canManage ? hallTimeAction(time) : null;
            return (
              <li
                key={`${time.windowId}-${time.date}`}
                className="flex items-start justify-between gap-3 rounded-md bg-primary/5 px-3 py-2"
              >
                <div className="min-w-0">
                  <p
                    className={
                      time.released || time.paused ? "text-sm text-slate-600 line-through" : "text-sm text-foreground"
                    }
                  >
                    {formatWhen(time)}
                  </p>
                  <p className="truncate text-sm text-foreground">{time.groupName ?? t("common.not_applicable")}</p>
                  {time.paused && (
                    <p className="text-xs text-slate-600">
                      {p(time.paused === "public" ? "paused_public" : "paused_school")}
                    </p>
                  )}
                  {time.released && !time.paused && <p className="text-xs text-slate-600">{p("released")}</p>}
                </div>
                {action && (
                  <Button variant="outline" size="sm" className="shrink-0" onClick={() => setPending({ time, action })}>
                    {p(action === "release" ? "release" : "restore")}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pending && (
        <div className="space-y-3 rounded-md border border-foreground/15 px-3 py-3">
          <p className="text-sm text-foreground">
            {p(pending.action === "release" ? "release_confirm" : "restore_confirm", {
              when: formatWhen(pending.time),
              room: pending.time.groupName ?? t("common.not_applicable"),
            })}
          </p>
          <p className="text-xs text-slate-600">
            {p(pending.action === "release" ? "release_consequence" : "restore_consequence")}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setPending(null)}>
              {p("keep")}
            </Button>
            <Button onClick={confirm} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {p(pending.action === "release" ? "release" : "restore")}
            </Button>
          </div>
        </div>
      )}

      {data.hasWindows && weeks < MAX_WEEKS && (
        <Button
          variant="outline"
          className="w-full"
          disabled={isFetching}
          onClick={() => setWeeks((w) => Math.min(w + MORE_WEEKS, MAX_WEEKS))}
        >
          {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {p("show_more")}
        </Button>
      )}

      {backToPlan}
    </AccountPageLayout>
  );
};

export default OrganizationHallTimesPage;
